import type {
  AssetDomain,
  BankAccount,
  FinancialItem,
  InvestmentCompany,
  RealEstateProperty,
} from '../types';
import {
  totalAssets,
  totalBankCash,
  totalInvestmentPortfolio,
  totalLiabilities,
  totalRealEstateEquity,
} from './calculations';

export type NetWorthSnapshot = {
  assets: number;
  liabilities: number;
  investments: number;
  bankCash: number;
  /** Net real-estate equity (market value − outstanding mortgage). */
  realEstate: number;
  netWorth: number;
};

export type NetWorthSnapshotByDomain = {
  personal: NetWorthSnapshot;
  business: NetWorthSnapshot;
  combined: NetWorthSnapshot;
};

export type ComputeNetWorthSnapshotInput = {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  bankAccounts: BankAccount[];
  realEstateItems?: RealEstateProperty[];
};

function itemDomain(item: FinancialItem): AssetDomain {
  return item.domain === 'business' ? 'business' : 'personal';
}

function filterFinancialItemsByDomain(
  items: FinancialItem[],
  domain?: AssetDomain
): FinancialItem[] {
  if (!domain) return items;
  return items.filter((item) => itemDomain(item) === domain);
}

function buildSnapshot(
  financialItems: FinancialItem[],
  investments: number,
  bankCash: number,
  realEstate: number
): NetWorthSnapshot {
  const assets = totalAssets(financialItems);
  const liabilities = totalLiabilities(financialItems);
  const netWorth = investments + assets - liabilities + bankCash + realEstate;
  return { assets, liabilities, investments, bankCash, realEstate, netWorth };
}

/**
 * Personal, business, and combined net-worth buckets.
 * v1: portfolio value and bank cash (Qonto) count toward business only;
 * real-estate equity counts toward personal only.
 */
export function computeNetWorthSnapshotsByDomain(
  input: ComputeNetWorthSnapshotInput
): NetWorthSnapshotByDomain {
  const portfolio = totalInvestmentPortfolio(input.companies);
  const bankCash = totalBankCash(input.bankAccounts);
  const realEstate = totalRealEstateEquity(input.realEstateItems ?? []);

  const personalItems = filterFinancialItemsByDomain(input.financialItems, 'personal');
  const businessItems = filterFinancialItemsByDomain(input.financialItems, 'business');

  const personal = buildSnapshot(personalItems, 0, 0, realEstate);
  const business = buildSnapshot(businessItems, portfolio, bankCash, 0);
  const combined = buildSnapshot(input.financialItems, portfolio, bankCash, realEstate);

  return { personal, business, combined };
}

/** Liabilities as % of gross assets (registry + portfolio + bank cash + real estate). */
export function computeDebtRatio(snapshot: NetWorthSnapshot): number | null {
  const { assets, liabilities, investments, bankCash, realEstate } = snapshot;
  if (liabilities === 0) return 0;
  const gross = assets + investments + bankCash + realEstate;
  if (gross <= 0) return null;
  return (liabilities / gross) * 100;
}

/** Registry + portfolio + bank cash + real estate; optional domain filters registry items only. */
export function computeNetWorthSnapshot(
  input: ComputeNetWorthSnapshotInput,
  domain?: AssetDomain
): NetWorthSnapshot {
  const financialItems = filterFinancialItemsByDomain(input.financialItems, domain);
  const investments = totalInvestmentPortfolio(input.companies);
  const bankCash = totalBankCash(input.bankAccounts);
  const realEstate = totalRealEstateEquity(input.realEstateItems ?? []);
  return buildSnapshot(financialItems, investments, bankCash, realEstate);
}
